import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryKey,
  usePatchApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty,
} from '@/api/generated/company-management-user/company-management-user'
import type { LocalizationEntityType } from '@/api/model/localizationEntityType'
import type {PlannedLanguages, TranslationMap} from "@/components/common/translations/types.ts";

/**
 * Buffers translations of *nested* properties (e.g. a subtype field, whose property name carries
 * the subtype id) until the parent form is saved — same deal as `useEntityTranslations`, but for
 * a property set that isn't known upfront: entries are added as the user edits them and loading
 * sits in `NestedTranslateButton` instead of a fixed `useQueries` list.
 *
 * Callers must only offer a nested property once its ids exist; the buffer is keyed by the
 * finished backend property, so nothing has to be resolved after the save.
 */
export function useNestedTranslations() {
  const queryClient = useQueryClient()
  const [edited, setEdited] = useState<Record<string, Record<string, string>>>({})
  const { mutateAsync: patchTranslations } = usePatchApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty()

  const [plannedState, setPlannedState] = useState<Record<string, PlannedLanguages>>({})
  const [serverTranslations, setServerTranslations] = useState<Record<string, Record<string, string>>>({})

  const get = useCallback(
    (property: string): Record<string, string> | undefined => edited[property],
    [edited]
  );
  const set = useCallback(
    (property: string, translations: Record<string, string>) => setEdited(prev => ({ ...prev, [property]: translations })),
    []
  );
  const getPlanned = useCallback(
    (property: string): PlannedLanguages => plannedState[property] ?? new Set(),
    [plannedState]
  );
  const setPlanned = useCallback(
    (property: string, langs: PlannedLanguages) => setPlannedState(prev => ({ ...prev, [property]: langs })),
    []
  );
  const getAllPlanned = useCallback(
    (): Record<string, PlannedLanguages> => plannedState,
    [plannedState]
  );
  const getAll = useCallback(
    (): Record<string, TranslationMap> => edited,
    [edited]
  );
  const reset = useCallback(
    () => {
      setEdited({})
      setPlannedState({})
    },
    [setEdited, setPlannedState]
  );

  const setServerLoaded = useCallback(
    (property: string, translations: Record<string, string>) => setServerTranslations(prev => ({ ...prev, [property]: translations })),
    []
  );

  const getExisting = useCallback(
    (property: string): Record<string, string> => ({ ...serverTranslations[property], ...edited[property] }),
    [serverTranslations, edited]
  );

  const save = useCallback(
    async (entityType: LocalizationEntityType, entityId: string) => {
      for (const [property, translations] of Object.entries(edited)) {
        await patchTranslations({ entityType, entityId, property, data: translations })
        await queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryKey(
            entityType, entityId, property,
          ),
        })
      }
    },
    [edited]
  );

  return { get, set, getPlanned, setPlanned, getAll, getAllPlanned, reset, save, setServerLoaded, getExisting }
}
